// @ts-nocheck
import React, { useState, useEffect, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import { useColumnResize } from './useColumnResize';
import { supabase } from '../../supabaseClient';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../types';
import type { AdminUser } from '../../types/admin';
import Papa from 'papaparse';
import { format } from 'date-fns';
import { useAdminToast } from './useAdminToast';
import { useDebounce } from './useDebounce';
import { AdminCopyButton } from './AdminCopyButton';
import { AdminQuickFilters } from './AdminQuickFilters';
import { AdminLoadingSkeleton } from './AdminLoadingSkeleton';
import { AdminDateRangeFilter } from './AdminDateRangeFilter';
import { AdminColumnToggle } from './AdminColumnToggle';
import { AdminImportDialog } from './AdminImportDialog';
import { AdminUserDetailModal } from './AdminUserDetailModal';
import { AdminBulkEmailDialog } from './AdminBulkEmailDialog';
import { exportToCSV, exportToJSON, exportToPDF } from './AdminExportUtils';
import { useAuditLog } from './useAuditLog';
import { AdminAdvancedSearch, type SearchRule, type SearchOperator } from './AdminAdvancedSearch';
import { AdminUserCard } from './AdminUserCard';
import { AdminTableRow } from './AdminTableRow';
import { AdminFilterPresets } from './AdminFilterPresets';
import type { FilterPreset } from './useFilterPresets';

interface AdminUsersProps {
  session: Session;
  profile: Profile | null;
}

export const AdminUsers: React.FC<AdminUsersProps> = ({ session, profile }) => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [updatingRole, setUpdatingRole] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editingFirstName, setEditingFirstName] = useState<string>('');
  const [editingLastName, setEditingLastName] = useState<string>('');
  const [activeQuickFilter, setActiveQuickFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [visibleColumns, setVisibleColumns] = useState<string[]>(['select', 'first_name', 'role', 'updated_at', 'id']);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [showBulkEmailDialog, setShowBulkEmailDialog] = useState(false);
  const [advancedSearchRules, setAdvancedSearchRules] = useState<SearchRule[]>([]);
  const [advancedSearchOperator, setAdvancedSearchOperator] = useState<SearchOperator>('AND');
  const [cardView, setCardView] = useState(false);
  const { showSuccess, showError } = useAdminToast();
  
  // Debounce search input
  const debouncedFilter = useDebounce(globalFilter, 300);
  
  // Column resizing with persistence
  const { columnSizing, saveColumnSizing } = useColumnResize('admin-users');
  
  // Audit logging
  const { logAction } = useAuditLog(session);

  useEffect(() => {
    loadUsers();

    // Set up real-time subscription
    const channel = supabase
      .channel('admin-users-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
        },
        () => {
          loadUsers();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Query profiles table - note: profiles table uses updated_at, not created_at
      // If we need created_at, we should join with auth.users
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, role, is_active, updated_at')
        .order('updated_at', { ascending: false })
        .limit(1000);

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, newRole: 'user' | 'admin' | 'super_admin') => {
    setUpdatingRole(userId);
    try {
      // Get old role for audit log
      const user = users.find((u) => u.id === userId);
      const oldRole = user?.role;

      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) throw error;
      
      // Log audit action
      await logAction('user_role_update', 'user', userId, {
        old_value: oldRole,
        new_value: newRole,
        user_email: user?.email,
        user_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Unknown',
      });

      await loadUsers();
      showSuccess(`User role updated to ${newRole}`);
    } catch (err: any) {
      console.error('Error updating user role:', err);
      showError(err.message || 'Failed to update user role');
    } finally {
      setUpdatingRole(null);
    }
  };

  const handleBulkRoleUpdate = async (newRole: 'user' | 'admin' | 'super_admin') => {
    if (selectedRows.length === 0) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .in('id', selectedRows);

      if (error) throw error;
      
      // Log audit action
      await logAction('user_bulk_role_update', 'user', selectedRows, {
        new_value: newRole,
        count: selectedRows.length,
      });

      setSelectedRows([]);
      await loadUsers();
      showSuccess(`${selectedRows.length} users updated to ${newRole}`);
    } catch (err: any) {
      console.error('Error bulk updating roles:', err);
      showError(err.message || 'Failed to bulk update roles');
    }
  };

  const handleBulkActivate = async (isActive: boolean) => {
    if (selectedRows.length === 0) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: isActive })
        .in('id', selectedRows);

      if (error) throw error;
      
      // Log audit action
      await logAction(
        isActive ? 'user_bulk_activate' : 'user_bulk_deactivate',
        'user',
        selectedRows,
        {
          new_value: isActive,
          count: selectedRows.length,
        }
      );

      setSelectedRows([]);
      await loadUsers();
      showSuccess(`${selectedRows.length} user(s) ${isActive ? 'activated' : 'deactivated'} successfully`);
    } catch (err: any) {
      console.error('Error bulk updating status:', err);
      showError(err.message || `Failed to ${isActive ? 'activate' : 'deactivate'} users`);
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: boolean | undefined) => {
    const newStatus = !(currentStatus !== false);
    try {
      const user = users.find((u) => u.id === userId);
      
      const { error } = await supabase
        .from('profiles')
        .update({ is_active: newStatus })
        .eq('id', userId);

      if (error) throw error;
      
      // Log audit action
      await logAction(
        newStatus ? 'user_activate' : 'user_deactivate',
        'user',
        userId,
        {
          old_value: currentStatus !== false,
          new_value: newStatus,
          user_email: user?.email,
          user_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Unknown',
        }
      );

      await loadUsers();
      showSuccess(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (err: any) {
      console.error('Error updating user status:', err);
      showError(err.message || 'Failed to update user status');
    }
  };

  const handleStartEditingName = (user: AdminUser) => {
    setEditingName(user.id);
    setEditingFirstName(user.first_name || '');
    setEditingLastName(user.last_name || '');
  };

  const handleSaveName = async (userId: string) => {
    try {
      const user = users.find((u) => u.id === userId);
      const oldFirstName = user?.first_name || '';
      const oldLastName = user?.last_name || '';

      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: editingFirstName.trim() || null,
          last_name: editingLastName.trim() || null,
        })
        .eq('id', userId);

      if (error) throw error;

      // Log audit action
      await logAction('user_name_update', 'user', userId, {
        old_first_name: oldFirstName,
        old_last_name: oldLastName,
        new_first_name: editingFirstName.trim(),
        new_last_name: editingLastName.trim(),
      });

      setEditingName(null);
      await loadUsers();
      showSuccess('User name updated successfully');
    } catch (err: any) {
      console.error('Error updating user name:', err);
      showError(err.message || 'Failed to update user name');
    }
  };

  const handleCancelEditingName = () => {
    setEditingName(null);
    setEditingFirstName('');
    setEditingLastName('');
  };

  // Helper function to check if a user matches a search rule
  const matchesRule = (user: AdminUser, rule: SearchRule): boolean => {
    const value = rule.value.toLowerCase().trim();
    if (!value) return true;

    let fieldValue = '';
    switch (rule.field) {
      case 'first_name':
        fieldValue = (user.first_name || '').toLowerCase();
        break;
      case 'last_name':
        fieldValue = (user.last_name || '').toLowerCase();
        break;
      case 'role':
        fieldValue = user.role.toLowerCase();
        break;
      case 'id':
        fieldValue = user.id.toLowerCase();
        break;
      case 'email':
        fieldValue = (user.email || '').toLowerCase();
        break;
      default:
        return true;
    }

    switch (rule.operator) {
      case 'contains':
        return fieldValue.includes(value);
      case 'equals':
        return fieldValue === value;
      case 'starts_with':
        return fieldValue.startsWith(value);
      case 'ends_with':
        return fieldValue.endsWith(value);
      case 'not_contains':
        return !fieldValue.includes(value);
      default:
        return true;
    }
  };

  // Apply quick filters, date range, and advanced search
  const filteredUsers = useMemo(() => {
    let filtered = users;
    
    if (activeQuickFilter === 'admins') {
      filtered = filtered.filter((u) => u.role === 'admin' || u.role === 'super_admin');
    } else if (activeQuickFilter === 'users') {
      filtered = filtered.filter((u) => u.role === 'user');
    } else if (activeQuickFilter === 'active') {
      filtered = filtered.filter((u) => u.is_active !== false);
    } else if (activeQuickFilter === 'inactive') {
      filtered = filtered.filter((u) => u.is_active === false);
    } else if (activeQuickFilter === 'recent') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      filtered = filtered.filter((u) => {
        if (!u.updated_at) return false;
        const userDate = new Date(u.updated_at);
        return !isNaN(userDate.getTime()) && userDate >= sevenDaysAgo;
      });
    }
    
    // Apply date range filter
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter((u) => {
        if (!u.updated_at) return false;
        const userDate = new Date(u.updated_at);
        return !isNaN(userDate.getTime()) && userDate >= dateRange.start! && userDate <= dateRange.end!;
      });
    }
    
    // Apply advanced search rules
    if (advancedSearchRules.length > 0) {
      const validRules = advancedSearchRules.filter((r) => r.value.trim() !== '');
      if (validRules.length > 0) {
        filtered = filtered.filter((user) => {
          if (advancedSearchOperator === 'AND') {
            // All rules must match
            return validRules.every((rule) => matchesRule(user, rule));
          } else {
            // At least one rule must match
            return validRules.some((rule) => matchesRule(user, rule));
          }
        });
      }
    }
    
    return filtered;
  }, [users, activeQuickFilter, dateRange, advancedSearchRules, advancedSearchOperator]);

  const quickFilters = [
    {
      id: 'all',
      label: 'All Users',
      icon: 'people',
      onClick: () => setActiveQuickFilter(undefined),
      count: users.length,
    },
    {
      id: 'admins',
      label: 'Admins',
      icon: 'admin_panel_settings',
      onClick: () => setActiveQuickFilter('admins'),
      count: users.filter((u) => u.role === 'admin' || u.role === 'super_admin').length,
    },
    {
      id: 'users',
      label: 'Regular Users',
      icon: 'person',
      onClick: () => setActiveQuickFilter('users'),
      count: users.filter((u) => u.role === 'user').length,
    },
    {
      id: 'active',
      label: 'Active',
      icon: 'check_circle',
      onClick: () => setActiveQuickFilter('active'),
      count: users.filter((u) => u.is_active !== false).length,
    },
    {
      id: 'inactive',
      label: 'Inactive',
      icon: 'cancel',
      onClick: () => setActiveQuickFilter('inactive'),
      count: users.filter((u) => u.is_active === false).length,
    },
    {
      id: 'recent',
      label: 'Recent (7d)',
      icon: 'schedule',
      onClick: () => setActiveQuickFilter('recent'),
      count: users.filter((u) => {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return new Date(u.created_at) >= sevenDaysAgo;
      }).length,
    },
  ];

  const handleExportCSV = () => {
    const dataToExport = selectedRows.length > 0 
      ? users.filter((u) => selectedRows.includes(u.id))
      : filteredUsers;
    
    const csvData = dataToExport.map((user) => ({
      Name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'No name',
      Role: user.role,
      Status: user.is_active !== false ? 'Active' : 'Inactive',
      'Last Updated': user.updated_at ? format(new Date(user.updated_at), 'yyyy-MM-dd HH:mm:ss') : 'N/A',
    }));
    exportToCSV(csvData, selectedRows.length > 0 ? 'selected_users' : 'users');
    showSuccess(
      selectedRows.length > 0
        ? `${selectedRows.length} selected user(s) exported to CSV successfully`
        : 'Users exported to CSV successfully'
    );
  };

  const handleExportJSON = () => {
    const dataToExport = selectedRows.length > 0 
      ? users.filter((u) => selectedRows.includes(u.id))
      : filteredUsers;
    
    exportToJSON(dataToExport, selectedRows.length > 0 ? 'selected_users' : 'users');
    showSuccess(
      selectedRows.length > 0
        ? `${selectedRows.length} selected user(s) exported to JSON successfully`
        : 'Users exported to JSON successfully'
    );
  };

  const handleExportPDF = () => {
    const dataToExport = selectedRows.length > 0 
      ? users.filter((u) => selectedRows.includes(u.id))
      : filteredUsers;
    
    const pdfData = dataToExport.map((user) => ({
      Name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'No name',
      Email: user.email || 'N/A',
      Role: user.role,
      Status: user.is_active !== false ? 'Active' : 'Inactive',
      'Last Updated': user.updated_at ? format(new Date(user.updated_at), 'yyyy-MM-dd HH:mm:ss') : 'N/A',
    }));

    exportToPDF(pdfData, {
      title: 'User Management Report',
      subtitle: selectedRows.length > 0 
        ? `${selectedRows.length} Selected Users`
        : `All Users (${dataToExport.length} total)`,
      filename: selectedRows.length > 0 ? 'selected_users' : 'users',
    });

    showSuccess(
      selectedRows.length > 0
        ? `${selectedRows.length} selected user(s) exported to PDF successfully`
        : 'Users exported to PDF successfully'
    );
  };

  const handleImport = async (data: any[]) => {
    try {
      // Transform imported data to match user structure
      const usersToImport = data.map((row) => ({
        first_name: row.first_name || row['First Name'] || '',
        last_name: row.last_name || row['Last Name'] || '',
        role: (row.role || row['Role'] || 'user') as 'user' | 'admin' | 'super_admin',
      }));

      // Import users (you may need to adjust this based on your API)
      const { error } = await supabase.from('profiles').upsert(usersToImport, {
        onConflict: 'id',
        ignoreDuplicates: false,
      });

      if (error) throw error;
      await loadUsers();
      showSuccess(`Successfully imported ${usersToImport.length} user(s)`);
    } catch (err: any) {
      console.error('Error importing users:', err);
      throw new Error(err.message || 'Failed to import users');
    }
  };

  const sampleImportData = [
    {
      'First Name': 'John',
      'Last Name': 'Doe',
      'Role': 'user',
    },
    {
      'First Name': 'Jane',
      'Last Name': 'Smith',
      'Role': 'admin',
    },
  ];

  const columnDefinitions = useMemo(() => [
    {
      id: 'select',
      label: 'Select',
      defaultVisible: true,
    },
    {
      id: 'first_name',
      label: 'Name',
      defaultVisible: true,
    },
    {
      id: 'role',
      label: 'Role',
      defaultVisible: true,
    },
    {
      id: 'is_active',
      label: 'Status',
      defaultVisible: true,
    },
    {
      id: 'created_at',
      label: 'Created',
      defaultVisible: true,
    },
    {
      id: 'updated_at',
      label: 'Last Updated',
      defaultVisible: true,
    },
    {
      id: 'id',
      label: 'ID',
      defaultVisible: true,
    },
  ], []);

  const columns = useMemo<ColumnDef<AdminUser>[]>(
    () => [
      {
        id: 'select',
        size: 50,
        minSize: 40,
        maxSize: 80,
        enableResizing: false,
        enableSorting: false,
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllPageRowsSelected()}
            onChange={table.getToggleAllPageRowsSelectedHandler()}
            className="rounded border-luna-secondary/30"
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            className="rounded border-luna-secondary/30"
          />
        ),
      },
      {
        accessorKey: 'first_name',
        header: 'Name',
        size: 180,
        minSize: 120,
        maxSize: 300,
        cell: ({ row }) => {
          const user = row.original;
          const isEditing = editingName === user.id;

          if (isEditing) {
            return (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
                <div className="flex flex-col sm:flex-row gap-2 flex-1">
                  <input
                    type="text"
                    value={editingFirstName}
                    onChange={(e) => setEditingFirstName(e.target.value)}
                    placeholder="First name"
                    className="flex-1 px-3 py-2.5 sm:px-2 sm:py-1 text-sm sm:text-xs rounded-lg sm:rounded border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark min-h-[44px] sm:min-h-[32px] touch-manipulation"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveName(user.id);
                      } else if (e.key === 'Escape') {
                        handleCancelEditingName();
                      }
                    }}
                    autoFocus
                  />
                  <input
                    type="text"
                    value={editingLastName}
                    onChange={(e) => setEditingLastName(e.target.value)}
                    placeholder="Last name"
                    className="flex-1 px-3 py-2.5 sm:px-2 sm:py-1 text-sm sm:text-xs rounded-lg sm:rounded border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark min-h-[44px] sm:min-h-[32px] touch-manipulation"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSaveName(user.id);
                      } else if (e.key === 'Escape') {
                        handleCancelEditingName();
                      }
                    }}
                  />
                </div>
                <div className="flex gap-2 sm:flex-col">
                  <button
                    onClick={() => handleSaveName(user.id)}
                    className="flex-1 sm:flex-none px-4 py-2.5 sm:px-2 sm:py-1 text-sm sm:text-xs bg-green-600 text-white rounded-lg sm:rounded hover:bg-green-700 min-h-[44px] sm:min-h-[32px] touch-manipulation flex items-center justify-center gap-1"
                    aria-label="Save name"
                  >
                    <span className="material-symbols-outlined text-base sm:text-sm">check</span>
                    <span className="sm:hidden">Save</span>
                  </button>
                  <button
                    onClick={handleCancelEditingName}
                    className="flex-1 sm:flex-none px-4 py-2.5 sm:px-2 sm:py-1 text-sm sm:text-xs bg-gray-500 text-white rounded-lg sm:rounded hover:bg-gray-600 min-h-[44px] sm:min-h-[32px] touch-manipulation flex items-center justify-center gap-1"
                    aria-label="Cancel editing"
                  >
                    <span className="material-symbols-outlined text-base sm:text-sm">close</span>
                    <span className="sm:hidden">Cancel</span>
                  </button>
                </div>
              </div>
            );
          }

          return (
            <div className="flex items-center gap-2 w-full">
              <button
                onClick={() => {
                  setSelectedUser(user);
                  setShowUserDetail(true);
                }}
                className="text-left hover:text-luna-secondary transition-colors cursor-pointer flex-1"
                aria-label={`View details for ${user.first_name || user.id}`}
              >
                <div>
                  {user.first_name || ''} {user.last_name || ''}
                  {!user.first_name && !user.last_name && (
                    <span className="text-luna-text-light/50 dark:text-luna-text-dark/50">
                      No name
                    </span>
                  )}
                </div>
              </button>
              <button
                onClick={() => handleStartEditingName(user)}
                className="px-3 py-2 sm:px-2 sm:py-1 text-sm sm:text-xs text-luna-text-light/50 dark:text-luna-text-dark/50 hover:text-luna-secondary transition-colors rounded-lg sm:rounded min-h-[44px] sm:min-h-[32px] touch-manipulation flex items-center justify-center"
                aria-label="Edit name"
                title="Edit name"
              >
                <span className="material-symbols-outlined text-base sm:text-sm">edit</span>
                <span className="ml-1 sm:hidden">Edit</span>
              </button>
            </div>
          );
        },
      },
      {
        accessorKey: 'role',
        header: 'Role',
        size: 120,
        minSize: 100,
        maxSize: 200,
        cell: ({ row }) => {
          const user = row.original;
          return (
            <select
              value={user.role}
              onChange={(e) =>
                updateUserRole(
                  user.id,
                  e.target.value as 'user' | 'admin' | 'super_admin'
                )
              }
              disabled={updatingRole === user.id}
              className="px-3 py-2.5 sm:px-2 sm:py-1 text-sm sm:text-xs rounded-lg sm:rounded border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark min-h-[44px] sm:min-h-[32px] touch-manipulation w-full sm:w-auto"
              aria-label={`Change role for user ${user.first_name || user.id}`}
              aria-busy={updatingRole === user.id}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          );
        },
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        size: 100,
        minSize: 80,
        maxSize: 150,
        cell: ({ row }) => {
          const user = row.original;
          const isActive = user.is_active !== false;
          return (
            <button
              onClick={() => handleToggleUserStatus(user.id, user.is_active)}
              className={`px-4 py-2.5 sm:px-3 sm:py-1.5 rounded-lg text-sm font-medium transition-colors min-h-[44px] sm:min-h-[32px] touch-manipulation w-full sm:w-auto ${
                isActive
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50'
              }`}
              aria-label={`${isActive ? 'Deactivate' : 'Activate'} user ${user.first_name || user.id}`}
            >
              {isActive ? 'Active' : 'Inactive'}
            </button>
          );
        },
      },
      {
        accessorKey: 'updated_at',
        header: 'Last Updated',
        size: 160,
        minSize: 140,
        maxSize: 220,
        cell: ({ getValue }) => {
          const date = getValue() as string | null | undefined;
          if (!date) return 'N/A';
          try {
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return 'Invalid date';
            return format(dateObj, 'MMM dd, yyyy HH:mm');
          } catch (e) {
            return 'Invalid date';
          }
        },
      },
      {
        accessorKey: 'id',
        header: 'ID',
        size: 150,
        minSize: 120,
        maxSize: 250,
        cell: ({ getValue }) => {
          const id = getValue() as string;
          return (
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                {id.substring(0, 8)}...
              </span>
              <AdminCopyButton text={id} label="User ID" />
            </div>
          );
        },
      },
    ],
    [updatingRole, editingName, editingFirstName, editingLastName, handleStartEditingName, handleSaveName, handleCancelEditingName]
  );

  const visibleColumnsData = useMemo(() => {
    return columns.filter((col) => {
      const colId = col.id || (col as any).accessorKey;
      return visibleColumns.includes(colId as string);
    });
  }, [columns, visibleColumns]);

  const table = useReactTable({
    data: filteredUsers,
    columns: visibleColumnsData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      globalFilter: debouncedFilter,
      columnSizing,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnSizingChange: saveColumnSizing,
    columnResizeMode: 'onChange',
    defaultColumn: {
      size: 150,
      minSize: 50,
      maxSize: 500,
    },
    initialState: {
      pagination: {
        pageSize: 20,
      },
    },
  });

  const handleToggleColumn = (columnId: string) => {
    setVisibleColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  };

  const handleToggleAllColumns = (visible: boolean) => {
    if (visible) {
      setVisibleColumns(columnDefinitions.map((col) => col.id));
    } else {
      setVisibleColumns(['select']); // Keep select column always visible
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="mb-6">
          <div className="h-8 bg-luna-secondary/20 rounded animate-pulse w-64 mb-2" />
          <div className="h-4 bg-luna-secondary/10 rounded animate-pulse w-96" />
        </div>
        <AdminLoadingSkeleton rows={10} columns={6} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
          User Management
        </h1>
        <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">
          Manage users and their roles
        </p>
      </div>

      {/* Quick Filters */}
      <AdminQuickFilters filters={quickFilters} activeFilter={activeQuickFilter} className="mb-4" />

      {/* Date Range Filter */}
      <AdminDateRangeFilter
        onDateRangeChange={(start, end) => setDateRange({ start, end })}
        className="mb-4"
      />

      {/* Advanced Search */}
      <AdminAdvancedSearch
        fields={[
          { id: 'first_name', label: 'First Name' },
          { id: 'last_name', label: 'Last Name' },
          { id: 'email', label: 'Email' },
          { id: 'role', label: 'Role' },
          { id: 'id', label: 'ID' },
        ]}
        onSearch={(rules, operator) => {
          setAdvancedSearchRules(rules);
          setAdvancedSearchOperator(operator);
          setGlobalFilter(''); // Clear simple search when using advanced
        }}
        onClear={() => {
          setAdvancedSearchRules([]);
          setAdvancedSearchOperator('AND');
        }}
        className="mb-4"
      />

      {/* Filter Presets */}
      <AdminFilterPresets
        tableId="admin-users"
        currentQuickFilter={activeQuickFilter}
        currentDateRange={dateRange}
        currentSearchRules={advancedSearchRules}
        currentSearchOperator={advancedSearchOperator}
        onApplyPreset={(preset: FilterPreset) => {
          if (preset.quickFilter) setActiveQuickFilter(preset.quickFilter);
          if (preset.dateRange) setDateRange(preset.dateRange);
          if (preset.searchRules) {
            setAdvancedSearchRules(preset.searchRules);
            setAdvancedSearchOperator(preset.searchOperator || 'AND');
            setGlobalFilter('');
          }
        }}
        onSaveCurrent={(name, quickFilter, dateRange, searchRules, searchOperator) => {
          // This is handled by AdminFilterPresets internally
        }}
        className="mb-4"
      />

      {/* Toolbar */}
      <div className="flex flex-col gap-4 bg-white dark:bg-luna-primary p-4 rounded-xl border-2 border-luna-secondary/30">
        <div className="w-full">
          <input
            type="text"
            placeholder="Search users..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="w-full px-4 py-2.5 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary text-luna-text-light dark:text-luna-text-dark min-h-[44px] touch-manipulation"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
          {selectedRows.length > 0 && (
            <>
            <button
              onClick={() => handleBulkRoleUpdate('admin')}
              className="w-full sm:w-auto px-4 py-2.5 bg-luna-secondary text-white rounded-lg hover:bg-luna-secondary/90 min-h-[44px] touch-manipulation"
              aria-label={`Make ${selectedRows.length} selected users admin`}
            >
              Make Admin ({selectedRows.length})
            </button>
            <button
              onClick={() => handleBulkRoleUpdate('user')}
              className="w-full sm:w-auto px-4 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 min-h-[44px] touch-manipulation"
              aria-label={`Make ${selectedRows.length} selected users regular users`}
            >
              Make User ({selectedRows.length})
            </button>
            <button
              onClick={() => handleBulkActivate(true)}
              className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 min-h-[44px] touch-manipulation"
              aria-label={`Activate ${selectedRows.length} selected users`}
            >
              Activate ({selectedRows.length})
            </button>
            <button
              onClick={() => handleBulkActivate(false)}
              className="w-full sm:w-auto px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 min-h-[44px] touch-manipulation"
              aria-label={`Deactivate ${selectedRows.length} selected users`}
            >
              Deactivate ({selectedRows.length})
            </button>
            <button
              onClick={() => setShowBulkEmailDialog(true)}
              className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
              aria-label={`Send email to ${selectedRows.length} selected users`}
            >
              <span className="material-symbols-outlined text-sm">email</span>
              <span>Send Email ({selectedRows.length})</span>
            </button>
            </>
          )}
          <div className="w-full sm:w-auto">
            <AdminColumnToggle
              columns={columnDefinitions}
              visibleColumns={visibleColumns}
              onToggle={handleToggleColumn}
              onToggleAll={handleToggleAllColumns}
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={() => setShowImportDialog(true)}
              className="w-full sm:w-auto px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
              title="Import users from CSV/JSON"
            >
              <span className="material-symbols-outlined text-sm">upload</span>
              <span>Import</span>
            </button>
            <button
              onClick={handleExportCSV}
              className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 min-h-[44px] touch-manipulation"
              title={selectedRows.length > 0 ? `Export ${selectedRows.length} selected users` : 'Export all users'}
            >
              <span className="sm:hidden">CSV</span>
              <span className="hidden sm:inline">Export CSV</span>
              {selectedRows.length > 0 && ` (${selectedRows.length})`}
            </button>
            <button
              onClick={handleExportJSON}
              className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 min-h-[44px] touch-manipulation"
              title={selectedRows.length > 0 ? `Export ${selectedRows.length} selected users` : 'Export all users'}
            >
              <span className="sm:hidden">JSON</span>
              <span className="hidden sm:inline">Export JSON</span>
              {selectedRows.length > 0 && ` (${selectedRows.length})`}
            </button>
            <button
              onClick={handleExportPDF}
              className="w-full sm:w-auto px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
              title={selectedRows.length > 0 ? `Export ${selectedRows.length} selected users to PDF` : 'Export all users to PDF'}
            >
              <span className="material-symbols-outlined text-sm">picture_as_pdf</span>
              <span className="sm:hidden">PDF</span>
              <span className="hidden sm:inline">Export PDF</span>
              {selectedRows.length > 0 && ` (${selectedRows.length})`}
            </button>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <button
          onClick={() => setCardView(!cardView)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] touch-manipulation flex items-center gap-2 ${
            cardView
              ? 'bg-luna-secondary text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-luna-text-light dark:text-luna-text-dark'
          }`}
          aria-label={cardView ? 'Switch to table view' : 'Switch to card view'}
        >
          <span className="material-symbols-outlined text-sm">
            {cardView ? 'table_rows' : 'view_module'}
          </span>
          <span className="hidden sm:inline">{cardView ? 'Table View' : 'Card View'}</span>
        </button>
      </div>

      {/* Card View */}
      {cardView ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUsers.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <span className="material-symbols-outlined text-4xl text-luna-text-light/30 dark:text-luna-text-dark/30 mb-4">
                people
              </span>
              <p className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                {globalFilter ? 'No users found matching your search' : 'No users found'}
              </p>
            </div>
          ) : (
            filteredUsers.map((user) => (
              <AdminUserCard
                key={user.id}
                user={user}
                isSelected={selectedRows.includes(user.id)}
                updatingRole={updatingRole}
                onSelect={(userId, selected) => {
                  if (selected) {
                    setSelectedRows([...selectedRows, userId]);
                  } else {
                    setSelectedRows(selectedRows.filter((id) => id !== userId));
                  }
                }}
                onRoleChange={updateUserRole}
                onStatusToggle={handleToggleUserStatus}
                onViewDetails={(user) => {
                  setSelectedUser(user);
                  setShowUserDetail(true);
                }}
              />
            ))
          )}
        </div>
      ) : (
        /* Table View */
        <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 overflow-hidden">
        <div className="overflow-x-auto -mx-4 sm:mx-0 overscroll-x-contain touch-pan-x">
          <div className="inline-block min-w-full align-middle px-4 sm:px-0">
            <table className="w-full min-w-[640px] sm:min-w-0" role="table" aria-label="Users table">
              <thead className="bg-luna-secondary/10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} role="row">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-3 sm:px-4 py-3 text-left text-xs sm:text-sm font-semibold text-luna-text-light dark:text-luna-text-dark whitespace-nowrap relative group"
                        role="columnheader"
                        scope="col"
                        style={{ width: header.getSize() }}
                      >
                        {header.column.getCanSort() ? (
                          <button
                            onClick={header.column.getToggleSortingHandler()}
                            className="flex items-center gap-2 w-full text-left hover:text-luna-secondary transition-colors group"
                            aria-label={`Sort by ${header.column.columnDef.header}`}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                            <span className="flex flex-col items-center justify-center">
                              {header.column.getIsSorted() === 'asc' ? (
                                <span className="material-symbols-outlined text-sm text-luna-secondary">arrow_upward</span>
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <span className="material-symbols-outlined text-sm text-luna-secondary">arrow_downward</span>
                              ) : (
                                <span className="material-symbols-outlined text-sm text-luna-text-light/30 dark:text-luna-text-dark/30 opacity-0 group-hover:opacity-100 transition-opacity">unfold_more</span>
                              )}
                            </span>
                          </button>
                        ) : (
                          <div className="flex items-center gap-2">
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </div>
                        )}
                        {header.column.getCanResize() && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none bg-transparent hover:bg-luna-secondary/50 transition-colors ${
                              header.column.getIsResizing() ? 'bg-luna-secondary' : ''
                            }`}
                            style={{
                              userSelect: 'none',
                              touchAction: 'none',
                            }}
                            aria-label="Resize column"
                            role="separator"
                            aria-orientation="vertical"
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={table.getAllColumns().length} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <span className="material-symbols-outlined text-4xl text-luna-text-light/30 dark:text-luna-text-dark/30 mb-4">
                        people
                      </span>
                      <p className="text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-2">
                        {globalFilter ? 'No users found matching your search' : 'No users found'}
                      </p>
                      <p className="text-xs text-luna-text-light/70 dark:text-luna-text-dark/70">
                        {globalFilter
                          ? 'Try adjusting your search query'
                          : 'Users will appear here once they sign up'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <AdminTableRow
                    key={row.id}
                    row={row}
                    selectedRows={selectedRows}
                    onSelect={(userId, selected) => {
                      if (selected) {
                        setSelectedRows([...selectedRows, userId]);
                      } else {
                        setSelectedRows(selectedRows.filter((id) => id !== userId));
                      }
                    }}
                  />
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* Pagination */}
        {table.getRowModel().rows.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 py-3 border-t border-luna-secondary/10">
            <div className="text-xs sm:text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
              Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                filteredUsers.length
              )}{' '}
              of {filteredUsers.length} users
              {selectedRows.length > 0 && ` (${selectedRows.length} selected)`}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="w-full sm:w-auto px-4 py-2.5 rounded border border-luna-secondary/30 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-manipulation"
                aria-label="Go to previous page"
              >
                Previous
              </button>
              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="w-full sm:w-auto px-4 py-2.5 rounded border border-luna-secondary/30 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-manipulation"
                aria-label="Go to next page"
              >
                Next
              </button>
            </div>
          </div>
        )}
        </div>
      )}

      {/* Import Dialog */}
      <AdminImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImport={handleImport}
        acceptedFormats={['.csv', '.json']}
        sampleData={sampleImportData}
        title="Import Users"
      />

      {/* User Detail Modal */}
      <AdminUserDetailModal
        isOpen={showUserDetail}
        user={selectedUser}
        onClose={() => {
          setShowUserDetail(false);
          setSelectedUser(null);
        }}
        onUserUpdate={loadUsers}
      />

      {/* Bulk Email Dialog */}
        <AdminBulkEmailDialog
          isOpen={showBulkEmailDialog}
          selectedUserIds={selectedRows}
          selectedUserEmails={users.filter((u) => selectedRows.includes(u.id)).map((u) => u.email || '').filter(Boolean)}
          onClose={() => setShowBulkEmailDialog(false)}
          onSent={async (subject: string, sentCount: number, failedCount: number) => {
            // Log audit action
            await logAction('bulk_email_send', 'user', selectedRows, {
              email_subject: subject,
              email_recipients: selectedRows.length,
              sent_count: sentCount,
              failed_count: failedCount,
            });
            setShowBulkEmailDialog(false);
            setSelectedRows([]);
          }}
        />
    </div>
  );
};

